<template>
	<button
		type="button"
		class="portal-nav-link"
		:class="{ 'is-active': active, 'is-collapsed': !expanded }"
		:aria-current="active ? 'page' : null"
		:aria-label="label"
		:content="label"
		v-tippy="{
			placement: 'right',
			boundary: 'window',
		}"
		@click="handleClick"
	>
		<span class="nav-icon-shell" aria-hidden="true">
			<custom-page-icon v-if="customIcon" class="nav-icon" :name="icon" :size="21"></custom-page-icon>
			<portal-icon v-else class="nav-icon" :name="icon" :size="21"></portal-icon>
		</span>
		<span class="nav-label" v-if="expanded">{{ label }}</span>
		<portal-icon v-if="expanded && hyperLink" class="external-icon" name="external-link" :size="15"></portal-icon>
	</button>
</template>

<script>
	import CustomPageIcon from '@/components/CustomPageIcon.vue';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'NavLink',

		components: { CustomPageIcon, PortalIcon },

		props: {
			label: { type: String, required: true },
			icon: { type: String, required: false, default: '' },
			customIcon: { type: Boolean, required: false, default: false },
			route: { type: String, required: false, default: '' },
			disableActive: { type: Boolean, required: false, default: false },
			altRoutes: { type: Array, required: false, default: () => [] },
			hyperLink: { type: String, required: false, default: '' },
		},

		mounted() {
			this.syncTooltipState();
		},

		methods: {
			syncTooltipState() {
				this.$nextTick(() => {
					const tooltip = this.$el && this.$el._tippy;
					if (!tooltip) return;
					if (this.expanded) {
						tooltip.hide(0);
						tooltip.disable();
					} else {
						tooltip.enable();
					}
				});
			},

			handleClick() {
				if (this.hyperLink) {
					window.open(this.hyperLink, '_blank', 'noopener');
					return;
				}
				if (this.route && this.$route.fullPath !== this.route) {
					this.$router.push(this.route);
				}
			},
		},

		computed: {
			expanded() {
				return this.$store.state.leftNavExpanded;
			},

			active() {
				if (this.disableActive || !this.route) {
					return false;
				}
				return [this.route, ...this.altRoutes].some((route) => {
					const routePath = route.split('?')[0];
					return this.$route.path === routePath || this.$route.path.startsWith(`${routePath}/`);
				});
			},
		},

		watch: {
			expanded() {
				this.syncTooltipState();
			},
		},
	};
</script>

<style scoped lang="scss">
	.portal-nav-link {
		position: relative;
		width: 100%;
		min-height: 42px;
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 5px 10px;
		border: 0;
		border-radius: var(--brand-button-radius, 9px);
		background: transparent;
		color: var(--portal-nav-muted);
		cursor: pointer;
		text-align: left;
		transition:
			background-color 160ms ease,
			color 160ms ease;
	}

	.portal-nav-link:hover {
		background: var(--v-gray_20-base);
		background: color-mix(in srgb, currentColor 6%, transparent);
		color: var(--portal-nav-text);
	}

	.portal-nav-link:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.portal-nav-link.is-active {
		background: var(--v-gray_20-base);
		background: color-mix(in srgb, currentColor 5%, transparent);
		color: var(--portal-nav-text);
		font-weight: 650;
	}

	.portal-nav-link.is-active::before {
		position: absolute;
		inset: 0;
		border: 1px solid var(--portal-nav-border);
		border-color: color-mix(in srgb, currentColor 15%, transparent);
		border-left: 4px solid var(--v-primary-base);
		border-radius: var(--brand-button-radius, 9px);
		content: '';
		pointer-events: none;
	}

	.portal-nav-link.is-collapsed {
		min-height: 44px;
		justify-content: center;
		padding: 6px;
	}

	.nav-icon-shell {
		width: 28px;
		height: 28px;
		flex: 0 0 28px;
		display: grid;
		place-items: center;
		color: currentColor;
		transition: color 160ms ease;
	}

	.portal-nav-link:hover .nav-icon-shell {
		color: currentColor;
	}

	.portal-nav-link.is-active .nav-icon-shell {
		color: currentColor;
	}

	.nav-icon {
		color: currentColor !important;
	}

	.nav-label {
		min-width: 0;
		flex: 1;
		overflow: hidden;
		font-size: 14px;
		font-weight: 560;
		line-height: 1.3;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.external-icon {
		flex: 0 0 auto;
		color: currentColor !important;
	}

	@media (prefers-reduced-motion: reduce) {
		.portal-nav-link {
			transition: none;
		}

		.nav-icon-shell {
			transition: none;
		}
	}
</style>
